import { useState, useEffect, useCallback, useRef } from "react";
import { supabase, SanPhamHanQuoc } from "../lib/supabase";

export interface ProductEntry {
  product_id: string;
  product_name: string;
  product_name_kr: string;
  product_image: string;
}

export interface CompanyWithProduct {
  company_name: string;
  company_logo: string;
  company_id: string;
  featured_product_name: string;
  featured_product_name_kr: string;
  featured_product_id: string;
  featured_product_image: string;
  // All products for this company in this category
  all_products: ProductEntry[];
}

interface NavbarDataReturn {
  categoryCounts: Record<string, number>;
  categoryProducts: Record<string, SanPhamHanQuoc[]>;
  companyProducts: Record<string, SanPhamHanQuoc[]>;
  companyProductCounts: Record<string, number>;
  companiesByCategory: Record<string, CompanyWithProduct[]>;
  loadingCounts: boolean;
  fetchProductsForCategory: (categoryId: string, categoryName?: string) => Promise<void>;
  fetchProductsForCompany: (companyName: string) => Promise<void>;
  fetchCompaniesByCategory: (categoryId: string, categoryName: string) => Promise<void>;
  prefetchAllCategories: (categoryList: { id: string; name: string }[]) => Promise<void>;
}

// Module-level cache: persists across component re-renders, cleared on page reload
const companiesCacheMap = new Map<string, CompanyWithProduct[]>();
const fetchingSet = new Set<string>(); // prevent duplicate concurrent fetches

export const useNavbarData = (): NavbarDataReturn => {
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [categoryProducts, setCategoryProducts] = useState<Record<string, SanPhamHanQuoc[]>>({});
  const [companyProducts, setCompanyProducts] = useState<Record<string, SanPhamHanQuoc[]>>({});
  const [companyProductCounts, setCompanyProductCounts] = useState<Record<string, number>>({});
  const [companiesByCategory, setCompaniesByCategory] = useState<Record<string, CompanyWithProduct[]>>(() => {
    // Initialize from module-level cache
    const initial: Record<string, CompanyWithProduct[]> = {};
    companiesCacheMap.forEach((v, k) => { initial[k] = v; });
    return initial;
  });
  const [loadingCounts, setLoadingCounts] = useState(false);
  const prefetchedRef = useRef(false);

  useEffect(() => {
    const fetchCounts = async () => {
      setLoadingCounts(true);
      try {
        // Fetch product counts
        const { data: productData } = await supabase
          .from("san_pham_han_quoc")
          .select("category_id, category_name, sub_category_id, company_name");

        // Fetch company counts by category_id
        const { data: companyData } = await supabase
          .from("companies")
          .select("category_id, main_category_name");

        const catCounts: Record<string, number> = {};
        const compCounts: Record<string, number> = {};

        if (productData) {
          (productData as { category_id?: string; category_name?: string; sub_category_id?: string; company_name?: string }[]).forEach((row) => {
            if (row.category_id) catCounts[row.category_id] = (catCounts[row.category_id] || 0) + 1;
            if (row.category_name) {
              const nameKey = `name:${row.category_name.toLowerCase()}`;
              catCounts[nameKey] = (catCounts[nameKey] || 0) + 1;
            }
            if (row.sub_category_id) {
              catCounts[row.sub_category_id] = (catCounts[row.sub_category_id] || 0) + 1;
            }
            if (row.company_name) compCounts[row.company_name] = (compCounts[row.company_name] || 0) + 1;
          });
        }

        // Add company counts with prefix "company:" to distinguish from product counts
        // Count unique companies per category_id from san_pham_han_quoc (since companies.category_id is often null)
        if (productData) {
          const companiesPerCat: Record<string, Set<string>> = {};
          (productData as { category_id?: string; category_name?: string; company_name?: string }[]).forEach((row) => {
            if (!row.company_name) return;
            if (row.category_id) {
              if (!companiesPerCat[row.category_id]) companiesPerCat[row.category_id] = new Set();
              companiesPerCat[row.category_id].add(row.company_name.trim().toLowerCase());
            }
            if (row.category_name) {
              const nameKey = `name:${row.category_name.toLowerCase()}`;
              if (!companiesPerCat[nameKey]) companiesPerCat[nameKey] = new Set();
              companiesPerCat[nameKey].add(row.company_name.trim().toLowerCase());
            }
          });
          Object.entries(companiesPerCat).forEach(([key, set]) => {
            catCounts[`company:${key}`] = set.size;
          });
        }

        // Also count from companies table if category_id is set
        if (companyData) {
          (companyData as { category_id?: string; main_category_name?: string }[]).forEach((row) => {
            if (row.category_id) {
              const key = `company:${row.category_id}`;
              catCounts[key] = (catCounts[key] || 0) + 1;
            }
            if (row.main_category_name) {
              const nameKey = `company:name:${row.main_category_name.toLowerCase()}`;
              catCounts[nameKey] = (catCounts[nameKey] || 0) + 1;
            }
          });
        }

        setCategoryCounts(catCounts);
        setCompanyProductCounts(compCounts);
      } catch {
        // silent
      } finally {
        setLoadingCounts(false);
      }
    };
    fetchCounts();
  }, []);

  // Helper: enrich products with company_logo from companies table if missing
  const enrichWithCompanyLogo = async (products: SanPhamHanQuoc[]): Promise<SanPhamHanQuoc[]> => {
    const missing = products.filter((p) => !p.company_logo && p.company_name);
    if (missing.length === 0) return products;
    const names = [...new Set(missing.map((p) => p.company_name!).filter(Boolean))];
    const { data: companiesData } = await supabase
      .from("companies")
      .select("name, logo_url")
      .in("name", names);
    if (!companiesData) return products;
    const logoByName = new Map((companiesData as { name: string; logo_url: string | null }[]).map((c) => [c.name, c.logo_url ?? ""]));
    return products.map((p) => {
      if (p.company_logo || !p.company_name) return p;
      const logo = logoByName.get(p.company_name);
      return logo ? { ...p, company_logo: logo } : p;
    });
  };

  const fetchProductsForCategory = useCallback(async (categoryId: string, categoryName?: string) => {
    if (!categoryId) return;
    if (categoryProducts[categoryId]) return;
    try {
      // If it's a sub_category_id (not a parent cat-xxx id), query by sub_category_id
      const isSubCat = !categoryId.startsWith("cat-");
      let query = supabase
        .from("san_pham_han_quoc")
        .select("id, name, name_kr, images, media_items, company_name, company_logo, category_id, sub_category_id, description")
        .order("created_at", { ascending: false })
        .limit(3);

      if (isSubCat && categoryName) {
        // Try sub_category_id first, then fallback to sub_category_name
        const { data: bySubId } = await supabase
          .from("san_pham_han_quoc")
          .select("id, name, name_kr, images, media_items, company_name, company_logo, category_id, sub_category_id, description")
          .eq("sub_category_id", categoryId)
          .order("created_at", { ascending: false })
          .limit(3);

        if (bySubId && bySubId.length > 0) {
          const enriched = await enrichWithCompanyLogo(bySubId as SanPhamHanQuoc[]);
          setCategoryProducts((prev) => ({ ...prev, [categoryId]: enriched }));
          return;
        }

        // Fallback: search by sub_category_name
        const { data: bySubName } = await supabase
          .from("san_pham_han_quoc")
          .select("id, name, name_kr, images, media_items, company_name, company_logo, category_id, sub_category_id, description")
          .ilike("sub_category_name", `%${categoryName}%`)
          .order("created_at", { ascending: false })
          .limit(3);

        if (bySubName && bySubName.length > 0) {
          const enriched = await enrichWithCompanyLogo(bySubName as SanPhamHanQuoc[]);
          setCategoryProducts((prev) => ({ ...prev, [categoryId]: enriched }));
          return;
        }

        // Final fallback: search by category_name
        const { data: byCatName } = await supabase
          .from("san_pham_han_quoc")
          .select("id, name, name_kr, images, media_items, company_name, company_logo, category_id, sub_category_id, description")
          .ilike("category_name", `%${categoryName}%`)
          .order("created_at", { ascending: false })
          .limit(3);
        const enriched = await enrichWithCompanyLogo((byCatName ?? []) as SanPhamHanQuoc[]);
        setCategoryProducts((prev) => ({ ...prev, [categoryId]: enriched }));
        return;
      }

      // Parent category: query by category_id
      if (categoryId.startsWith("cat-") && categoryName) {
        query = query.ilike("category_name", `%${categoryName}%`);
      } else {
        query = query.eq("category_id", categoryId);
      }

      const { data } = await query;
      if (data && data.length > 0) {
        const enriched = await enrichWithCompanyLogo(data as SanPhamHanQuoc[]);
        setCategoryProducts((prev) => ({ ...prev, [categoryId]: enriched }));
      } else if (categoryName) {
        const { data: dataByName } = await supabase
          .from("san_pham_han_quoc")
          .select("id, name, name_kr, images, media_items, company_name, company_logo, category_id, sub_category_id, description")
          .ilike("category_name", `%${categoryName}%`)
          .order("created_at", { ascending: false })
          .limit(3);
        const enriched = await enrichWithCompanyLogo((dataByName ?? []) as SanPhamHanQuoc[]);
        setCategoryProducts((prev) => ({ ...prev, [categoryId]: enriched }));
      } else {
        setCategoryProducts((prev) => ({ ...prev, [categoryId]: [] }));
      }
    } catch {
      setCategoryProducts((prev) => ({ ...prev, [categoryId]: [] }));
    }
  }, [categoryProducts]);

  const fetchProductsForCompany = useCallback(async (companyName: string) => {
    if (!companyName || companyProducts[companyName]) return;
    try {
      const { data } = await supabase
        .from("san_pham_han_quoc")
        .select("id, name, name_kr, images, company_name, company_logo, category_id, description")
        .eq("company_name", companyName)
        .order("created_at", { ascending: false })
        .limit(3);
      if (data) {
        setCompanyProducts((prev) => ({ ...prev, [companyName]: data as SanPhamHanQuoc[] }));
      }
    } catch {
      // silent
    }
  }, [companyProducts]);

  /**
   * Fetch companies for a specific category from Supabase.
   * Uses module-level cache to avoid redundant network calls.
   * Returns empty array [] for categories with no products (enables conditional rendering).
   */
  const fetchCompaniesByCategory = useCallback(async (categoryId: string, categoryName: string) => {
    if (!categoryId) return;

    // Return from cache immediately (no re-fetch needed)
    if (companiesCacheMap.has(categoryId)) {
      const cached = companiesCacheMap.get(categoryId)!;
      setCompaniesByCategory((prev) => ({ ...prev, [categoryId]: cached }));
      return;
    }

    // Prevent duplicate concurrent fetches for same category
    if (fetchingSet.has(categoryId)) return;
    fetchingSet.add(categoryId);

    try {
      // Step 1: Query products filtered by category
      let productData: SanPhamHanQuoc[] | null = null;

      if (!categoryId.startsWith("cat-")) {
        const { data } = await supabase
          .from("san_pham_han_quoc")
          .select("id, name, name_kr, images, company_name, company_id, category_id, category_name")
          .eq("category_id", categoryId)
          .order("created_at", { ascending: false })
          .limit(50);
        productData = data as SanPhamHanQuoc[] | null;
      }

      // Fallback: search by category name
      if ((!productData || productData.length === 0) && categoryName) {
        const { data: fallback } = await supabase
          .from("san_pham_han_quoc")
          .select("id, name, name_kr, images, company_name, company_id, category_id, category_name")
          .ilike("category_name", `%${categoryName}%`)
          .order("created_at", { ascending: false })
          .limit(50);
        productData = (fallback as SanPhamHanQuoc[]) ?? [];
      }

      // No products → cache empty array (conditional rendering: hide dropdown)
      if (!productData || productData.length === 0) {
        companiesCacheMap.set(categoryId, []);
        setCompaniesByCategory((prev) => ({ ...prev, [categoryId]: [] }));
        return;
      }

      // Step 2: Group by company_name (always use company_name as key to avoid
      // duplicate entries when company_id is null for some products of the same company)
      const companyMap = new Map<string, CompanyWithProduct>();
      productData.forEach((p) => {
        if (!p.company_name) return;
        // Always use company_name as the grouping key for consistency
        const key = p.company_name.trim().toLowerCase();
        const img = Array.isArray(p.images) && p.images.length > 0 ? (p.images[0] as string) : "";
        if (!companyMap.has(key)) {
          companyMap.set(key, {
            company_name: p.company_name,
            company_logo: "",
            // Prefer non-null company_id; will be overwritten if a later product has one
            company_id: p.company_id ?? "",
            featured_product_name: p.name,
            featured_product_name_kr: p.name_kr ?? p.name,
            featured_product_id: p.id,
            featured_product_image: img,
            all_products: [],
          });
        } else if (!companyMap.get(key)!.company_id && p.company_id) {
          // Fill in company_id if it was missing from the first product
          companyMap.get(key)!.company_id = p.company_id;
        }
        // Add this product to the company's product list
        companyMap.get(key)!.all_products.push({
          product_id: p.id,
          product_name: p.name,
          product_name_kr: p.name_kr ?? p.name,
          product_image: img,
        });
      });

      // Step 3: Fetch logo_url from companies table
      const companyIds = Array.from(companyMap.values()).map((c) => c.company_id).filter(Boolean);
      const companyNames = Array.from(companyMap.values()).map((c) => c.company_name);

      let companiesData: { id: string; name: string; logo_url: string | null }[] = [];

      if (companyIds.length > 0) {
        const { data: byId } = await supabase
          .from("companies")
          .select("id, name, logo_url")
          .in("id", companyIds);
        if (byId) companiesData = byId as typeof companiesData;
      }

      if (companyNames.length > 0) {
        const { data: byName } = await supabase
          .from("companies")
          .select("id, name, logo_url")
          .in("name", companyNames);
        if (byName) {
          (byName as typeof companiesData).forEach((c) => {
            if (!companiesData.find((x) => x.id === c.id)) companiesData.push(c);
          });
        }
      }

      // Step 4: Merge logo_url into result
      const logoById = new Map(companiesData.map((c) => [c.id, c.logo_url ?? ""]));
      const logoByName = new Map(companiesData.map((c) => [c.name, c.logo_url ?? ""]));

      const result = Array.from(companyMap.values()).map((c) => ({
        ...c,
        company_logo:
          (c.company_id && logoById.get(c.company_id)) ||
          logoByName.get(c.company_name) ||
          "",
      }));

      // Cache and update state
      companiesCacheMap.set(categoryId, result);
      setCompaniesByCategory((prev) => ({ ...prev, [categoryId]: result }));
    } catch {
      // On error, cache empty to avoid infinite retries
      companiesCacheMap.set(categoryId, []);
      setCompaniesByCategory((prev) => ({ ...prev, [categoryId]: [] }));
    } finally {
      fetchingSet.delete(categoryId);
    }
  }, []);

  /**
   * Prefetch all categories at once (called when company flyout opens).
   * Runs in parallel for fast loading, skips already-cached categories.
   */
  const prefetchAllCategories = useCallback(async (categoryList: { id: string; name: string }[]) => {
    if (prefetchedRef.current) return;
    prefetchedRef.current = true;

    // Fetch all uncached categories in parallel
    const uncached = categoryList.filter((cat) => !companiesCacheMap.has(cat.id));
    if (uncached.length === 0) return;

    await Promise.allSettled(
      uncached.map((cat) => fetchCompaniesByCategory(cat.id, cat.name))
    );
  }, [fetchCompaniesByCategory]);

  return {
    categoryCounts,
    categoryProducts,
    companyProducts,
    companyProductCounts,
    companiesByCategory,
    loadingCounts,
    fetchProductsForCategory,
    fetchProductsForCompany,
    fetchCompaniesByCategory,
    prefetchAllCategories,
  };
};
